import Icon from "./Icon.jsx";
import Chip from "./Chip.jsx";
import {
  truncate,
  estimatedValue,
  suggestedAngle,
  evidenceChips,
  previewLine,
} from "../lib/format.js";

/**
 * The core of the product. Fulminio picks one company and states the move,
 * rather than listing prospects and leaving the judgement to the user.
 *
 * Three columns: what to do, why this company, what the move is.
 * The outreach preview lives inside the same card so the promise is visible
 * before anything is generated.
 */
export default function FulminioDecision({
  best,
  signals,
  onDraft,
  onPitch,
  drafting,
  pitching,
  draft,
  onDismissDraft,
}) {
  if (!best) return null;

  const angle = suggestedAngle(best);
  const chips = evidenceChips(best, signals);

  return (
    <div
      className="a"
      style={{
        background:
          "linear-gradient(135deg,rgba(245,197,24,0.05) 0%,rgba(245,197,24,0.015) 60%,transparent 100%)",
        border: "1px solid rgba(245,197,24,0.14)",
        borderRadius: 16,
        overflow: "hidden",
        marginBottom: 24,
      }}
    >
      <div
        style={{
          padding: "12px 22px",
          borderBottom: "1px solid rgba(245,197,24,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            background: "var(--vg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="bolt" size={14} color="var(--volt)" fill />
        </div>
        <span
          style={{
            fontFamily: "var(--d)",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--volt)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          Fulminio Decision
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--t3)" }}>
          Updated just now
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr 1fr" }}>
        {/* Decision */}
        <div style={{ padding: "20px 22px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
          <Label>Decision</Label>
          <div
            style={{
              fontFamily: "var(--d)",
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              marginBottom: 10,
            }}
          >
            Focus on {best.company} today.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "5px 11px",
                borderRadius: 100,
                background: "var(--volt)",
                color: "#0A0A0F",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Best shot today
            </span>
            <span style={{ fontSize: 13, color: "var(--t3)" }}>{best.sector}</span>
          </div>

          <div style={{ marginBottom: 14 }}>
            <Label>Estimated value</Label>
            <div
              style={{
                fontFamily: "var(--d)",
                fontSize: 26,
                fontWeight: 700,
                color: "var(--volt)",
                letterSpacing: "-0.02em",
              }}
            >
              {estimatedValue(best)}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              fontSize: 13,
              paddingTop: 12,
              borderTop: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <Metric label="Fit" value={best.fit || best.score} />
            <Metric label="Timing" value={best.trigger || best.score} />
            <Metric label="Reach" value={best.reach || best.score} />
          </div>
        </div>

        {/* Why this company */}
        <div style={{ padding: "20px 22px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
          <Label>Why this company</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {chips.map((c, i) => (
              <Chip key={i} label={c.label} color={c.color} />
            ))}
          </div>
          <div style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6 }}>
            {truncate(best.why_now, 140)}
          </div>
        </div>

        {/* Recommended move */}
        <div style={{ padding: "20px 22px" }}>
          <Label>Recommended move</Label>
          <button
            onClick={() => onDraft(best)}
            disabled={drafting === best.id}
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 9,
              border: "none",
              background: "var(--volt)",
              color: "#0A0A0F",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "var(--d)",
              cursor: drafting === best.id ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <Icon name="mail" size={15} color="#0A0A0F" />
            {drafting === best.id ? "Generating…" : `Generate ${angle.toLowerCase()} intro`}
          </button>
          <button
            onClick={() => onPitch(best)}
            disabled={pitching === best.id}
            style={{
              width: "100%",
              padding: 11,
              borderRadius: 9,
              border: "1px solid var(--brd)",
              background: "transparent",
              color: "var(--t2)",
              fontSize: 13,
              fontWeight: 500,
              cursor: pitching === best.id ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              marginBottom: 12,
            }}
          >
            <Icon name="doc" size={14} color="var(--t3)" />
            {pitching === best.id
              ? "Building…"
              : best.has_pitch
              ? "View pitch"
              : "Build pitch"}
          </button>
          <div style={{ fontSize: 12, color: "var(--t3)", lineHeight: 1.55 }}>
            Lead with a short audit, not a generic intro.
          </div>
        </div>
      </div>

      {/* Outreach preview */}
      <div style={{ padding: "14px 22px 18px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <Label>Outreach preview</Label>
        {draft && !draft.error ? (
          <>
            <div style={{ fontSize: 13, color: "var(--t3)", marginBottom: 4 }}>
              To:{" "}
              <span style={{ color: "var(--t1)" }}>
                {draft.to_name} &lt;{draft.to}&gt;
              </span>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>{draft.subject}</div>
            <div
              style={{
                fontSize: 14,
                color: "var(--t2)",
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                padding: "14px 16px",
                background: "var(--s2)",
                borderRadius: 9,
                borderLeft: "2px solid var(--volt)",
                marginBottom: 12,
              }}
            >
              {draft.body}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <GhostButton
                onClick={() =>
                  navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`)
                }
              >
                Copy
              </GhostButton>
              <button
                onClick={() =>
                  window.open(
                    `mailto:${draft.to}?subject=${encodeURIComponent(
                      draft.subject
                    )}&body=${encodeURIComponent(draft.body)}`
                  )
                }
                style={{
                  padding: "7px 14px",
                  borderRadius: 7,
                  border: "none",
                  background: "var(--volt)",
                  color: "#0A0A0F",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "var(--d)",
                  cursor: "pointer",
                }}
              >
                Send
              </button>
              <GhostButton onClick={onDismissDraft} style={{ marginLeft: "auto" }}>
                Dismiss
              </GhostButton>
            </div>
          </>
        ) : draft?.error ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--red)",
              padding: "10px 14px",
              background: "rgba(239,68,68,0.05)",
              borderRadius: 8,
            }}
          >
            No verified contact for this company yet.
          </div>
        ) : (
          <div
            style={{
              fontSize: 14,
              color: "var(--t2)",
              fontStyle: "italic",
              padding: "14px 16px",
              background: "var(--s2)",
              borderRadius: 9,
              borderLeft: "2px solid rgba(245,197,24,0.25)",
              lineHeight: 1.7,
            }}
          >
            “{previewLine(best)}”
          </div>
        )}
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "var(--t3)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <span style={{ color: "var(--t3)" }}>{label} </span>
      <strong style={{ color: "var(--t1)", fontFamily: "var(--d)", fontSize: 15 }}>{value}</strong>
    </div>
  );
}

function GhostButton({ children, onClick, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 14px",
        borderRadius: 7,
        border: "1px solid var(--brd)",
        background: "transparent",
        color: "var(--t2)",
        fontSize: 12,
        cursor: "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}
